/**
 * Adapter WebSocket pour balance BAXTRAN SL
 * Utilisé pour les navigateurs sans Web Serial API (Safari, Firefox)
 * Communique avec le Scale Bridge local (localhost:3334 HTTPS) qui gère le port série
 *
 * Le bridge tourne sur chaque Mac Mini et expose la balance USB via WebSocket
 */

import { io, Socket } from 'socket.io-client';
import { ScaleAdapter, WeightReading, ScaleError } from './ScaleAdapter';
import { logger } from '../../utils/logger';

export class WebSocketScaleAdapter implements ScaleAdapter {
  readonly modelName = 'BAXTRAN SL (WebSocket)';
  readonly supportsAutoConnect = false;

  private socket: Socket | null = null;
  private subscribers: Array<(reading: WeightReading) => void> = [];
  private connected = false;
  private portPath: string;

  constructor(portPath?: string) {
    // Pas de défaut Linux/macOS hardcodé — si vide/undefined, le bridge utilisera
    // sa propre config locale (config.scale.port) et son auto-détection par pattern.
    this.portPath = portPath || '';
  }

  /**
   * Connecter à la balance via WebSocket
   */
  async connect(): Promise<void> {
    try {
      // Déterminer l'URL du backend
      const backendUrl = this.getBackendUrl();

      // Créer la connexion WebSocket
      // 5 tentatives au départ : une machine SANS bridge (Mac de dev, navigateur
      // hors caisse) arrête vite de spammer la console. Dès la 1re connexion
      // réussie (= vraie caisse), on passe en reconnexion infinie : le bridge
      // est en localhost et reviendra toujours (restart, KeepAlive).
      this.socket = io(`${backendUrl}/scales`, {
        transports: ['websocket', 'polling'],
        reconnection: true,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        reconnectionAttempts: 5,
      });

      // Attendre la connexion
      await new Promise<void>((resolve, reject) => {
        if (!this.socket) {
          reject(new ScaleError('Impossible de créer la connexion WebSocket', 'CONNECTION_ERROR'));
          return;
        }

        this.socket.on('connect', () => {
          logger.log('[WebSocketScaleAdapter] WebSocket connected');
          // Vraie caisse détectée → reconnexion infinie pour survivre aux
          // restarts du bridge
          this.socket!.io.opts.reconnectionAttempts = Infinity;

          // Demander la connexion à la balance — si portPath vide, on laisse
          // le bridge choisir le bon port via sa config locale / auto-détection
          this.socket!.emit('scale:connect', this.portPath ? { port: this.portPath } : {});
        });

        // Après 5 échecs sans jamais avoir été connecté = pas de bridge sur
        // cette machine → on arrête là, silencieusement
        this.socket.io.on('reconnect_failed', () => {
          logger.warn('[WebSocketScaleAdapter] Bridge introuvable — balance désactivée sur cette machine');
        });

        this.socket.on('scale:connected', (data) => {
          logger.log('[WebSocketScaleAdapter] Scale connected:', data);
          this.connected = true;
          resolve();
        });

        this.socket.on('scale:error', (error) => {
          logger.error('[WebSocketScaleAdapter] Scale error:', error);
          reject(new ScaleError(error.message, 'CONNECTION_ERROR'));
        });

        this.socket.on('scale:weight', (reading: WeightReading) => {
          // Le Hardware Bridge retourne le poids en kg, convertir en grammes
          // pour être cohérent avec l'interface ScaleAdapter (weight en grammes)
          const normalizedReading: WeightReading = {
            ...reading,
            weight: reading.unit === 'kg' ? reading.weight * 1000 : reading.weight,
            unit: 'g',
            timestamp: reading.timestamp || Date.now(),
          };
          // Notifier tous les abonnés
          this.subscribers.forEach((callback) => callback(normalizedReading));
        });

        this.socket.on('disconnect', (reason) => {
          logger.log('[WebSocketScaleAdapter] WebSocket disconnected:', reason);
          this.connected = false;
        });

        // Auto-reconnexion : quand Socket.IO se reconnecte, ré-attacher la balance
        this.socket.io.on('reconnect', () => {
          logger.log('[WebSocketScaleAdapter] Socket.IO reconnected — re-attaching scale');
          this.socket!.emit('scale:connect', this.portPath ? { port: this.portPath } : {});
        });

        let connectErrorLogged = false;
        this.socket.on('connect_error', (error) => {
          // Une seule erreur en console — les tentatives suivantes en silence
          if (!connectErrorLogged) {
            connectErrorLogged = true;
            logger.warn('[WebSocketScaleAdapter] Bridge injoignable (tentatives silencieuses en cours):', error?.message || error);
          }
          reject(new ScaleError(
            'Impossible de se connecter au serveur',
            'CONNECTION_ERROR',
            error
          ));
        });

        // Timeout
        setTimeout(() => {
          if (!this.connected) {
            reject(new ScaleError('Timeout lors de la connexion', 'CONNECTION_ERROR'));
          }
        }, 10000);
      });
    } catch (error) {
      if (error instanceof ScaleError) throw error;
      throw new ScaleError(
        'Erreur de connexion à la balance',
        'CONNECTION_ERROR',
        error as Error
      );
    }
  }

  /**
   * Déconnecter la balance
   */
  async disconnect(): Promise<void> {
    if (this.socket) {
      this.socket.emit('scale:disconnect');
      this.socket.disconnect();
      this.socket = null;
    }

    this.connected = false;
    this.subscribers = [];
    logger.log('[WebSocketScaleAdapter] Disconnected');
  }

  /**
   * Vérifier si la balance est connectée
   */
  isConnected(): boolean {
    return this.connected && this.socket !== null;
  }

  /**
   * Lire le poids actuel
   */
  async readWeight(): Promise<WeightReading> {
    if (!this.isConnected()) {
      throw new ScaleError('Balance non connectée', 'NOT_CONNECTED');
    }

    return new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        unsubscribe();
        reject(new ScaleError('Timeout lors de la lecture du poids', 'READ_ERROR'));
      }, 5000);

      const unsubscribe = this.subscribe((reading) => {
        clearTimeout(timeout);
        unsubscribe();
        resolve(reading);
      });

      // Demander une lecture
      this.socket!.emit('scale:read');
    });
  }

  /**
   * Tarrer la balance
   */
  async tare(): Promise<void> {
    if (!this.isConnected() || !this.socket) {
      throw new ScaleError('Balance non connectée', 'NOT_CONNECTED');
    }

    return new Promise((resolve, reject) => {
      this.socket!.emit('scale:tare');

      this.socket!.once('scale:tared', () => {
        logger.log('[WebSocketScaleAdapter] TARE success');
        resolve();
      });

      this.socket!.once('scale:error', (error) => {
        reject(new ScaleError(error.message, 'READ_ERROR'));
      });

      // Timeout
      setTimeout(() => {
        reject(new ScaleError('Timeout lors du TARE', 'READ_ERROR'));
      }, 5000);
    });
  }

  /**
   * S'abonner aux changements de poids
   */
  subscribe(callback: (reading: WeightReading) => void): () => void {
    this.subscribers.push(callback);

    // Retourner fonction de désabonnement
    return () => {
      const index = this.subscribers.indexOf(callback);
      if (index > -1) {
        this.subscribers.splice(index, 1);
      }
    };
  }

  /**
   * Définir le port série à utiliser
   */
  setPort(port: string): void {
    this.portPath = port;
  }

  /**
   * Obtenir l'URL du Scale Bridge local (HTTPS uniquement)
   */
  private getBackendUrl(): string {
    if (import.meta.env.VITE_SCALE_BRIDGE_URL) {
      return import.meta.env.VITE_SCALE_BRIDGE_URL;
    }
    return 'https://localhost:3334';
  }
}
